<template>
  <div :class="{'unavailable': signature && $nuxt.isOffline && !signature.base64}">
    <template v-if="options.type == 'croquis'">
      {{ $t('signatureShow.referenceDrawing') }}
    </template>

    <template v-else>
      {{ $t('signatureShow.signature') }} <strong>{{ options.created_name }}</strong> {{ $t('signatureShow.signedAs') }}
      {{ options.typeOfUser }}
    </template>

    <span style="display:block;
      color: rgb(146, 146, 146);
      font-size: 12px !important;
      white-space: nowrap;
      padding: 0px !important;">
      <template v-if="signature">
        {{ $t('signatureShow.datePrefix') }} {{ $moment(signature.createdAt).locale($i18n.locale).format($t('signatureShow.dateFormat')) }}
      </template>
      <template v-else>
        {{ $t('signatureShow.datePrefix') }} {{ $moment().locale($i18n.locale).format($t('signatureShow.dateFormat')) }}
      </template>
    </span>

    <div style="border-bottom: 1px dashed #ccc; padding-top: 7px; text-align: center;"
      @click.stop="sign">
      &nbsp;

      <template v-if="signature">
        <img v-if="$nuxt.isOffline && signature.base64" style="max-width: 95%; margin: 0 auto;"
          :src="signature.base64"
          alt="firma-offline"
        />

        <p v-if="$nuxt.isOffline && !signature.base64" style="font-weight: bold">
          {{ $t('signatureShow.previouslySigned') }}<br>
          {{ $t('signatureShow.cannotDisplayOffline') }}
        </p>

        <img style="max-width: 95%; margin: 0 auto;"
          v-if="!$nuxt.isOffline"
          :src="signature.base64 || signature.contentUrl || signature.public_url"
          alt="firma-online"
        />
      </template>
    </div>
  </div>
</template>


<script>
/**
 * @project: certiapp-nuxt
 * @file:    \components\signatures\show.vue
 * @desc:    ...
 * -------------------------------------------
 * Created Date: 23rd August 2022
 * Last Modified: Fri May 05 2023
 **/

export default {
  name: 'SignatureShow',
  props: ['signed', 'options'],

  data: () => ({
    user: {},
    signature: false,

    ui: {
      valid: false,
      dialog: false,
      saving: false,
      loading: false,
      tokenBusquedaFirma: 0,
    },
  }),

  computed: {
    permiteAbrirComponenteFirma() {
      return this.options?.permiteEditarFirma !== false
    },
    permiteReemplazarFirmaExistente() {
      return this.options?.puedeReemplazarFirma === true
    },
  },

  watch: {
    signed: {
      handler() {
        this.findSignature()
      },
      deep: true,
    },
    options: {
      handler() {
        this.findSignature()
      },
      deep: true,
    },
  },

  methods: {
    esUuid(valor) {
      return (
        typeof valor === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
          valor
        )
      )
    },

    normalizarEndpointFirma(endpoint) {
      if (!endpoint || typeof endpoint !== 'string') {
        throw new Error('SignatureShow: la referencia de firma no es una cadena válida.')
      }

      if (endpoint.startsWith('/api/')) return endpoint.replace('/api/', '')
      if (endpoint.startsWith('api/')) return endpoint.replace('api/', '')
      if (endpoint.startsWith('/firmas/')) return endpoint.replace('/', '')
      if (endpoint.startsWith('firmas/')) return endpoint
      if (this.esUuid(endpoint)) return `firmas/${endpoint}`

      throw new Error(
        `SignatureShow: referencia de firma no soportada: "${endpoint}".`
      )
    },

    extraerReferenciaFirma(firma) {
      if (!firma) return null

      if (typeof firma === 'string') return firma
      if (typeof firma !== 'object') return null

      if (typeof firma['@id'] === 'string' && firma['@id']) return firma['@id']
      if (typeof firma.uuid === 'string' && firma.uuid) return firma.uuid

      return null
    },

    firmaTieneImagen(firma) {
      return Boolean(firma?.base64 || firma?.contentUrl || firma?.public_url)
    },

    async cargarFirmaPorReferencia(referenciaFirma) {
      const endpointNormalizado = this.normalizarEndpointFirma(referenciaFirma)
      const respuesta = await this.$axios.get(endpointNormalizado)

      if (!respuesta?.data || typeof respuesta.data !== 'object') {
        throw new Error(
          'SignatureShow: la API devolvió una firma inválida al resolver referencia.'
        )
      }

      return respuesta.data
    },

    async resolverFirmaCompleta(firma) {
      if (!firma) return null

      if (typeof firma === 'object' && this.firmaTieneImagen(firma)) {
        return firma
      }

      const referenciaFirma = this.extraerReferenciaFirma(firma)
      if (!referenciaFirma) {
        throw new Error(
          'SignatureShow: no se pudo extraer una referencia válida para cargar la firma.'
        )
      }

      return this.cargarFirmaPorReferencia(referenciaFirma)
    },

    coincideContextoFirma(opcionesFirmaEvento) {
      if (!opcionesFirmaEvento) return false

      return (
        opcionesFirmaEvento.modulo === this.options.modulo &&
        opcionesFirmaEvento.moduloRef === this.options.moduloRef &&
        opcionesFirmaEvento.type === this.options.type &&
        opcionesFirmaEvento.created_by === this.options.created_by &&
        opcionesFirmaEvento.user === this.options.user &&
        opcionesFirmaEvento.representante === this.options.representante
      )
    },

    buscarFirmaEnColeccion() {
      if (!Array.isArray(this.signed)) {
        console.error('SignatureShow: se esperaba un array de firmas para buscar coincidencias.', {
          signed: this.signed,
          options: this.options,
        })
        return false
      }

      if (this.options?.user) {
        return (
          this.signed.find((firma) => firma?.user?.['@id'] === this.options.user) || false
        )
      }

      if (this.options?.representante) {
        return (
          this.signed.find(
            (firma) => firma?.representante?.['@id'] === this.options.representante
          ) || false
        )
      }

      console.error(
        'SignatureShow: falta options.user o options.representante para buscar la firma en una coleccion.',
        { options: this.options }
      )
      return false
    },

    //+-------------------------------------------------
    // sign()
    // emits the signature event to open and display
    // the signature pad
    // -----
    // Created on Mon Aug 29 2022
    //+-------------------------------------------------
    sign() {
      if (!this.permiteAbrirComponenteFirma) return
      if (this.signature && !this.permiteReemplazarFirmaExistente) return

      this.$nuxt.$emit('signatures:open', {
        ...this.options,
        firmaExistente: this.signature,
      })
    },

    //+-------------------------------------------------
    // findSignature()
    // Searches this.signed prop for signatures that have
    // the same created_by as the current prop
    // -----
    // Created on Fri Sep 02 2022
    //+-------------------------------------------------
    async findSignature(event) {
      const tokenBusquedaActual = ++this.ui.tokenBusquedaFirma
      let signature = false

      if (event) {
        if (!this.coincideContextoFirma(event.opcionesFirma)) return

        if (!event.data || typeof event.data !== 'object') {
          console.error('SignatureShow: evento signatures:signed sin data valida.', event)
          return
        }

        signature = event.data
      } else if (Array.isArray(this.signed)) {
        signature = this.buscarFirmaEnColeccion()
      } else if (this.signed && typeof this.signed === 'object') {
        signature = this.signed
      } else if (typeof this.signed === 'string' && this.signed) {
        signature = this.signed
      }

      if (signature) {
        try {
          const firmaCompleta = await this.resolverFirmaCompleta(signature)

          if (tokenBusquedaActual !== this.ui.tokenBusquedaFirma) return
          this.display(firmaCompleta)
        } catch (errorResolviendoFirma) {
          if (tokenBusquedaActual !== this.ui.tokenBusquedaFirma) return

          console.error(
            'SignatureShow: no se pudo resolver la firma para mostrarla.',
            {
              signed: this.signed,
              options: this.options,
              signature,
              error: errorResolviendoFirma,
            }
          )
        }
      } else if (event) {
        console.error('SignatureShow: no se pudo resolver la firma del evento recibido.', {
          signed: this.signed,
          options: this.options,
          event,
        })
      }
    },

    //+-------------------------------------------------
    // display()
    // Retrieves the signature from data or events and show
    // -----
    // Created on Mon Aug 29 2022
    //+-------------------------------------------------
    display(body) {
      if (!body || typeof body !== 'object') {
        console.error('SignatureShow: se intento mostrar una firma invalida.', body)
        return
      }

      this.signature = body
    },

    async init() {
      this.user = this.$auth.user
      await this.findSignature()

      this.$nuxt.$on('signatures:signed', async (payload) => {
        await this.findSignature(payload)
        this.$emit('onSigned', payload)
      })
    },
  },

  mounted() {
    this.init()
  },

  beforeDestroy() {
    this.$nuxt.$off('signatures:signed')
  },
}
</script>

<style>
.unavailable {
  background: repeating-linear-gradient(
    -45deg,
    rgba(96, 108, 188, 0.25),
    rgba(96, 109, 188, 0.25) 10px,
    rgba(70, 82, 152, 0.25) 10px,
    rgba(70, 82, 152, 0.25) 20px
  );
  padding: 15px;
  border-radius: 6px;
}
</style>
